import React, { useEffect, useRef, useState } from "react";
import { Spinner } from "@material-tailwind/react";

/**
 * Flipbook vía zinejs. En mobile: single-page + contenedor que evita corte lateral.
 */
export default function RevistaZineFlipbook({ pdfUrl, onFail }) {
  const hostRef = useRef(null);
  const wrapRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!pdfUrl || !hostRef.current) return undefined;
    let cancelled = false;
    let zine = null;
    const hostEl = hostRef.current;

    (async () => {
      setLoading(true);
      setError("");
      try {
        const [{ Zine }, { PdfSource }] = await Promise.all([
          import("@zinejs/core"),
          import("@zinejs/pdf"),
        ]);
        if (cancelled || !hostRef.current) return;

        const workerSrc = `${window.location.origin}${
          process.env.PUBLIC_URL || ""
        }/pdf.worker.min.mjs`;

        const isMobile = window.matchMedia("(max-width: 768px)").matches;

        zine = new Zine(hostEl, {
          source: new PdfSource(pdfUrl, {
            progressive: true,
            preload: 1,
            disableAutoFetch: true,
            workerSrc,
            cdnFallback: false,
          }),
          singlePage: isMobile,
        });

        const resize = () => {
          try {
            zine?.resize?.();
          } catch {
            /* ignore */
          }
        };
        window.addEventListener("resize", resize);
        window.addEventListener("orientationchange", resize);
        // keep cleanup refs on zine object
        zine.__cleanupListeners = () => {
          window.removeEventListener("resize", resize);
          window.removeEventListener("orientationchange", resize);
        };

        if (!cancelled) setLoading(false);
        requestAnimationFrame(resize);
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          const msg = String(err?.message || err || "No se pudo iniciar el flipbook");
          setError(msg);
          setLoading(false);
          if (typeof onFail === "function") onFail(msg);
        }
      }
    })();

    return () => {
      cancelled = true;
      try {
        zine?.__cleanupListeners?.();
        zine?.destroy?.();
      } catch {
        /* ignore */
      }
      if (hostEl) hostEl.innerHTML = "";
    };
  }, [pdfUrl, onFail]);

  return (
    <div
      ref={wrapRef}
      className="relative w-full h-full min-h-0 min-w-0 bg-black overflow-hidden"
    >
      {loading ? (
        <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-black text-white/80">
          <Spinner className="h-8 w-8" />
          <p className="text-sm">Preparando Conexión Siete…</p>
        </div>
      ) : null}
      {error ? (
        <div className="absolute inset-0 z-10 flex items-center justify-center px-6">
          <p className="text-sm text-red-300 text-center max-w-md">{error}</p>
        </div>
      ) : null}
      {/*
        max-w-full + overflow-hidden: evita que el canvas del flipbook
        desborde el viewport en mobile (páginas cortadas a los lados).
      */}
      <div
        ref={hostRef}
        className="revista-zine-host w-full h-full max-w-full overflow-hidden flex items-center justify-center [&_*]:max-w-full [&_canvas]:max-w-full [&_canvas]:!h-auto"
      />
    </div>
  );
}
